<template>
  <div class="passenger-form" v-if="fields.length > 0">
    <b-row>
      <template v-for="(field, field_index) in fields">
        <div
          v-if="field_index === 0"
          class="passenger-form__title col-12"
          :key="`field_static_0_${passenger_index}_${field_index}`"
        >
          <passenger-icon
            :passenger="passenger"
            :color="sass('gray-700')"
            :size="18"
          />
          <span>{{ `${field.label} ${passenger_index + 1}` }}</span>
          <span class="passenger-form__age"> ({{ age_label }}) </span>
        </div>

        <!-- Gender -->
        <div
          v-if="field.name === 'gender'"
          class="passenger-form__field col-6 col-lg-3 col-xl-2"
          :key="`field_static_${field.name}_${passenger_index}_${field_index}`"
        >
          <validation-provider
            ref="gender_provider"
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_static_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>
            <div class="passenger-form__select">
              <vb-select
                :class="field.name"
                v-model="field.value"
                :vb_options="{
                  options: field.options,
                  persistent_key: build_input_id(passenger, field.name),
                }"
              />
            </div>
            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Name -->
        <div
          v-else-if="field.name === 'name'"
          class="passenger-form__field col-12 col-lg-3"
          :key="`field_static_name_${passenger_index}_${field_index}`"
        >
          <validation-provider
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_static_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>
            <vb-input
              v-model="field.value"
              :vb_options="{
                placeholder: field.placeholder,
                autocomplete: field.autocomplete,
                name: field.name,
                persistent_key: build_input_id(passenger, field.name),
              }"
            />
            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Surname -->
        <div
          v-else-if="field.name === 'surname'"
          class="passenger-form__field col-12 col-lg-3"
          :key="`field_static_lastname_${passenger_index}_${field_index}`"
        >
          <validation-provider
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_static_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>
            <vb-input
              v-model="field.value"
              :vb_options="{
                placeholder: field.placeholder,
                autocomplete: field.autocomplete,
                name: field.name,
                persistent_key: build_input_id(passenger, field.name),
              }"
            />
            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Birthday -->
        <div
          v-else-if="field.name === 'birthday'"
          class="passenger-form__field col-12 col-lg-3 col-xl-4"
          :key="`field_static_birthdate_${passenger_index}_${field_index}`"
        >
          <validation-provider
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_static_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>
            <div class="passenger-form__calendar">
              <birthdate-input
                v-model="field.value"
                :min="field.calendar.min"
                :max="field.calendar.max"
                :input_id="build_input_id(passenger, field.name)"
                :persistent_key="build_input_id(passenger, field.name)"
                :corrected="!!birthdate_correction(field.name)"
                @corrected="
                  (correction) =>
                    notify_birthdate_correction(
                      field.name,
                      correction,
                      declared_age
                    )
                "
              />
            </div>
            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Generic required fields -->
        <div
          class="passenger-form__field col-12 col-lg-3 col-xl-2"
          :key="`field_${passenger_index}_${field_index}`"
          v-else-if="field.validate.required && !field.hidden"
        >
          <validation-provider
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>

            <div
              v-if="field.type === 'calendar'"
              class="passenger-form__calendar"
            >
              <birthdate-input
                v-model="field.value"
                :min="field.calendar.min"
                :max="field.calendar.max"
                :input_id="build_input_id(passenger, field.name)"
                :persistent_key="build_input_id(passenger, field.name)"
                :corrected="!!birthdate_correction(field.name)"
                @corrected="
                  (correction) =>
                    notify_birthdate_correction(
                      field.name,
                      correction,
                      declared_age
                    )
                "
              />
            </div>

            <vb-input
              v-else-if="field.type === 'text'"
              :key="`input_${passenger_index}_${field_index}`"
              v-model="field.value"
              :vb_options="{
                placeholder: field.placeholder,
                autocomplete: field.autocomplete,
                name: field.name,
                persistent_key: build_input_id(passenger, field.name),
              }"
            />

            <div
              v-else-if="field.type === 'select'"
              class="passenger-form__select"
            >
              <vb-select
                :class="field.name === 'type_pax' ? 'vb-select--readonly' : ''"
                :key="`select_${passenger_index}_${field_index}`"
                v-model="field.value"
                :disabled="field.disabled"
                :vb_options="{
                  options: field.options,
                  persistent_key:
                    field.name !== 'type_pax'
                      ? build_input_id(passenger, field.name)
                      : undefined,
                }"
              /><span
                v-if="field.name === 'type_pax'"
                v-html="
                  passenger.age < 30
                    ? `(${passenger.age} ${passenger.age > 1 ? 'anni' : 'anno'})`
                    : ''
                "
              ></span>
            </div>

            <vb-checkbox
              v-else-if="field.type === 'checkbox'"
              v-model="field.value"
              :vb_options="{ text: field.placeholder }"
            />

            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>
      </template>
    </b-row>
  </div>
</template>

<script>
import { build_store_key } from "@/utilities/persistent-inputs";
import { DEFAULT_ADULT_AGE } from "@resources/passenger";
import { birthdate_correction_mixin } from "@/utilities/birthdate-correction";
import BirthdateInput from "@components/blocks/birthdate-input.vue";
import PassengerIcon from "@alpitour/components/atoms/passenger-icon.vue";

export default {
  name: "passenger-form",
  components: {
    "birthdate-input": BirthdateInput,
    "passenger-icon": PassengerIcon,
  },
  props: {
    passenger: {
      type: Object,
      required: true,
    },
    passenger_index: {
      type: Number,
      required: true,
    },
  },
  mixins: [birthdate_correction_mixin],
  computed: {
    // Solo quando l'eta' restringe i bound a un anno: gli adulti (compreso il
    // default a 30) tengono la fascia larga, quindi non c'e' un'eta' da citare.
    declared_age() {
      const { age } = this.passenger || {};
      if (typeof age !== "number" || age >= DEFAULT_ADULT_AGE || age >= 18) {
        return null;
      }
      return age;
    },
    fields() {
      const { details = {} } = this.passenger || {};
      return details.fields || [];
    },
    age_category() {
      if (this.passenger.age <= 1) return "Neonato";
      if (this.passenger.age >= 18) return "Adulto";
      return "Bambino";
    },
    age_label() {
      if (this.passenger.age === DEFAULT_ADULT_AGE) return this.age_category;
      const unit = this.passenger.age > 1 ? "anni" : "anno";
      return `${this.age_category} ${this.passenger.age} ${unit}`;
    },
    passenger_name() {
      return (this.fields.find((f) => f.name === "name") || {}).value;
    },
    passenger_surname() {
      return (this.fields.find((f) => f.name === "surname") || {}).value;
    },
    passenger_gender() {
      return (this.fields.find((f) => f.name === "gender") || {}).value;
    },
  },
  watch: {
    passenger_name() {
      this.maybe_trigger_gender_validation();
    },
    passenger_surname() {
      this.maybe_trigger_gender_validation();
    },
  },
  methods: {
    build_store_key,
    build_input_id(passenger, field_name) {
      return this.build_store_key(
        passenger.id,
        passenger.passengerType,
        field_name,
      );
    },
    maybe_trigger_gender_validation() {
      if (
        this.passenger_name &&
        this.passenger_surname &&
        !this.passenger_gender
      ) {
        this.$nextTick(() => {
          const ref = this.$refs.gender_provider;
          const provider = Array.isArray(ref) ? ref[0] : ref;
          if (provider && typeof provider.validate === "function") {
            provider.validate();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.passenger-form {
  background-color: $white;
  border-radius: 5px;
  padding: $grid-gutter-width/2;

  &__field {
    margin-bottom: $grid-gutter-width/2;
  }

  &__title {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-weight: $vb-font-weight-semibold;
    margin-bottom: $grid-gutter-width/2;
    color: $gray-700;
    padding: ($grid-gutter-width/4) ($grid-gutter-width/2)
      ($grid-gutter-width/2);
  }

  &__age-detail {
    margin-left: 0.25rem;
  }

  &__label {
    display: flex;
    gap: 5px;
    align-items: center;
    margin-left: $grid-gutter-width / 3;
    font-size: $font-size-md;
  }

  &__select {
    display: flex;
    align-items: center;

    .vb-select--readonly {
      width: auto;
      padding-right: $grid-gutter-width/10;
    }

    span {
      white-space: nowrap;
    }
  }

  &__birth-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    input {
      pointer-events: none;
      cursor: pointer;
      background: $white;
    }
  }
}
</style>
